import {
  CanConsumptionDashboardResponse,
  CanConsumptionMetrics
} from "@shared/interfaces/can-consumption-dashboard.interface";

import { DashboardPeriod } from "./can-consumption-period";

/**
 * Contract of `POST vehicles/get-can-consumption-dashboard`.
 *
 * The payload itself lives in the Shared library; this module only re-exports it next to the page
 * and adds the request shape and the empty payload the screen starts from.
 *
 * Whenever a field is `null` the backend is saying "there is no basis of calculation" — it MUST
 * render as a dash, never as a zero: on a consumption panel a zero reads as "burned nothing".
 */
export type {
  CanConsumptionMetrics,
  CanConsumptionDashboardVehicle,
  CanConsumptionDashboardOrigin,
  CanConsumptionDashboardDay,
  CanConsumptionDashboardResponse
} from "@shared/interfaces/can-consumption-dashboard.interface";

/**
 * One thing the filter kept.
 *
 * The label travels with the id because the panel names the selection on its header line and only
 * the drawer ever holds the option lists.
 */
export interface CanConsumptionDashboardFilterOption {
  /** uuid the endpoint expects */
  id: string;
  /** Label already translated by the drawer */
  label: string;
}

/**
 * What the filter drawer of the panel gives back.
 * An empty array means "every one of them", exactly as an absent parameter does.
 */
export interface CanConsumptionDashboardFilter {
  period: DashboardPeriod;
  /** Plants whose travels to keep, by `reg_locations.id_location` */
  origins: CanConsumptionDashboardFilterOption[];
  /** Equipment types to keep, by `tpe_vehicles_types.id_vehicle_type` */
  vehicleTypes: CanConsumptionDashboardFilterOption[];
}

export interface CanConsumptionDashboardRequest {
  /** ISO date-time of the first instant of the period */
  startDate: string;
  /** ISO date-time of the last instant of the period */
  finishDate: string;
  /** `reg_locations.id_location` (uuid) of the plants whose travels to keep */
  idOrigins?: string[];
  idRegionals?: string[];
  idGroups?: string[];
  /** `tpe_vehicles_types.id_vehicle_type` (uuid) */
  idVehicleTypes?: string[];
}

/** Used before the first query returns and whenever the filter matches nothing. */
export const EMPTY_FLEET_METRICS: CanConsumptionMetrics = {
  travelsCount: 0,
  volumeM3: null,
  distanceKm: null,
  fuelLiters: null,
  fuelInsideTravelsLiters: null,
  fuelBetweenTravelsLiters: null,
  engineHoursBetweenTravels: null,
  idleEngineHoursBetweenTravels: null,
  litersPerM3: null,
  litersPerTravel: null,
  kmPerLiter: null,
  litersPerHour: null,
  idleLitersPerHour: null,
  avgKmPerTravel: null,
  avgSpeedKmh: null,
  avgTravelMinutes: null
};

export const EMPTY_DASHBOARD: CanConsumptionDashboardResponse = {
  fleet: EMPTY_FLEET_METRICS,
  vehicles: [],
  origins: [],
  dailySeries: []
};
